import React, { useState, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  orgHeatmapData, 
  getColorByScore, 
  getHeatmapInterventions 
} from '../data/heatmapLogic';

export const OrganizationalHeatmap = () => {
  const [view, setView] = useState('org'); // 'org', 'dept'
  const [selectedDept, setSelectedDept] = useState(null);

  const treemapData = useMemo(() => {
    return orgHeatmapData.departments.map(dept => ({
      name: dept.name,
      value: dept.teams.length,
      health: dept.health,
      itemStyle: { color: getColorByScore(dept.health).hex }
    }));
  }, []);

  const treemapOption = {
    tooltip: {
      formatter: (info) => {
        const data = info.data;
        return `
          <div style="padding: 8px;">
            <b style="font-size: 14px;">${data.name}</b><br/>
            Helse-index: <b style="color:${data.itemStyle.color}">${data.health}%</b><br/>
            Antall team: ${data.value}
          </div>
        `;
      }
    },
    series: [{
      type: 'treemap',
      data: treemapData,
      leafDepth: 1,
      label: { show: true, formatter: '{b}\n{c} Team', fontSize: 14, fontWeight: 'bold' },
      upperLabel: { show: true, height: 30 },
      itemStyle: { borderColor: '#fff', borderWidth: 2, gapWidth: 2 },
      breadcrumb: { show: false }
    }]
  };

  const activeNode = selectedDept || orgHeatmapData;
  const interventions = useMemo(() => getHeatmapInterventions(activeNode), [activeNode]);

  return (
    <div className="space-y-8 pb-20">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 uppercase tracking-tight">Organizational Heatmap</h1>
          <p className="text-slate-500 mt-1">Hierarkisk visualisering av arbeidsmiljø-risiko.</p>
        </div>
        <div className="flex gap-2">
          {view === 'dept' && (
            <Button variant="secondary" onClick={() => {setView('org'); setSelectedDept(null);}} icon={<SafeIcon name="ArrowLeft" />}>
              Tilbake til Org
            </Button>
          )}
          <Button variant="primary" icon={<SafeIcon name="Download" />}>Eksporter PDF</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Heatmap Visualization */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="h-[500px]">
            <CardHeader 
              title={view === 'org' ? "Organisasjonsoversikt" : `Avdeling: ${selectedDept.name}`} 
              subtitle="Klikk på en boks for å drille ned"
              icon={<SafeIcon name="Grid" />}
            />
            <CardContent className="h-[400px]">
              {view === 'org' ? (
                <ReactECharts 
                  option={treemapOption} 
                  style={{ height: '100%' }} 
                  onEvents={{
                    'click': (params) => {
                      const dept = orgHeatmapData.departments.find(d => d.name === params.name);
                      if (dept) {
                        setSelectedDept(dept);
                        setView('dept');
                      }
                    }
                  }}
                />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 h-full overflow-y-auto pr-2 custom-scrollbar">
                  {selectedDept.teams.map((team, i) => {
                    const status = getColorByScore(team.health);
                    return (
                      <motion.div
                        key={team.name}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: i * 0.05 }}
                        className={`p-6 rounded-3xl border-2 border-transparent hover:border-${status.color}-200 bg-${status.color}-50 flex flex-col justify-between transition-all cursor-pointer group`}
                      >
                        <div>
                          <h4 className="font-black text-slate-800 uppercase tracking-tighter text-sm mb-1">{team.name}</h4>
                          <div className={`text-3xl font-black text-${status.color}-600`}>{team.health}%</div>
                        </div>
                        <div className="pt-4 border-t border-slate-200/50 mt-4">
                          <div className="flex justify-between text-[10px] font-bold text-slate-400 uppercase">
                            <span>Stress</span>
                            <span className={team.metrics.stress > 70 ? 'text-rose-500' : ''}>{team.metrics.stress}%</span>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Legend */}
          <div className="flex justify-center gap-8 py-4 bg-white rounded-2xl border border-slate-100 shadow-sm">
            {[
              { label: 'Healthy', color: 'bg-emerald-500' },
              { label: 'Moderate', color: 'bg-amber-400' },
              { label: 'High Risk', color: 'bg-orange-500' },
              { label: 'Critical', color: 'bg-rose-500' }
            ].map(l => (
              <div key={l.label} className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${l.color}`} />
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{l.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Sidebar: Risk Overview & Actions */}
        <div className="space-y-6">
          <Card>
            <CardHeader title="Risiko-profil" subtitle={activeNode.name} />
            <CardContent className="space-y-6">
              <div className="space-y-4">
                {[
                  { label: 'Work Environment', key: 'we', color: 'indigo' },
                  { label: 'Stress Index', key: 'stress', color: 'rose' },
                  { label: 'Psych Safety', key: 'safety', color: 'emerald' },
                  { label: 'Conflict Risk', key: 'conflict', color: 'orange' }
                ].map(m => {
                  const val = activeNode.metrics?.[m.key] || 0;
                  return (
                    <div key={m.key} className="space-y-1">
                      <div className="flex justify-between text-[10px] font-black text-slate-400 uppercase">
                        <span>{m.label}</span>
                        <span>{val}%</span>
                      </div>
                      <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                        <div className={`h-full bg-${m.color}-500 transition-all duration-1000`} style={{ width: `${val}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <div className="space-y-4">
            <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider ml-1">Anbefalte Intervensjoner</h3>
            <AnimatePresence mode="wait">
              {interventions.length > 0 ? interventions.map((inv, i) => (
                <motion.div
                  key={inv.title}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="p-5 bg-white rounded-2xl border border-slate-200 shadow-sm space-y-2 border-l-4 border-l-indigo-500"
                >
                  <div className="flex justify-between items-center">
                    <h4 className="font-bold text-slate-900">{inv.title}</h4>
                    <span className={`text-[9px] font-black px-2 py-1 rounded bg-rose-50 text-rose-600 uppercase`}>{inv.priority}</span>
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed">{inv.desc}</p>
                </motion.div>
              )) : (
                <div className="text-center py-10 bg-emerald-50 rounded-2xl border border-emerald-100">
                  <SafeIcon name="CheckCircle" className="text-emerald-500 mx-auto mb-2 text-2xl" />
                  <p className="text-xs font-bold text-emerald-700">Ingen kritiske avvik for denne noden.</p>
                </div>
              )}
            </AnimatePresence>
          </div>

          <Card className="bg-slate-900 border-none text-white">
            <CardContent className="p-6">
              <h4 className="font-bold mb-2">Trenger du dypere innsikt?</h4>
              <p className="text-xs text-slate-400 leading-relaxed mb-4">
                Kombiner heatmap med AI-diagnose for å avdekke skjulte kulturelle mønstre.
              </p>
              <Button variant="secondary" className="w-full bg-white/10 border-white/20 text-white text-xs">
                Kjør AI-analyse
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};